import { isNil, throttle } from 'lodash-es';
import {
  IAutorunOptions,
  IReactionPublic,
  action,
  autorun,
  comparer,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { useEffect } from 'react';

export class AsyncFnState<
  Fn extends (...args: any[]) => Promise<any> = (
    ...args: any[]
  ) => Promise<any>,
> {
  @observable loading: boolean = false;
  @observable error: unknown = null;
  @observable data?: Awaited<ReturnType<Fn>> = undefined;
  // 是否已经请求过了
  @observable hasFired: boolean = false;
  private nonce: number = 0;
  private fn: Fn;
  private _defaultState?: Awaited<ReturnType<Fn>>;
  constructor(fn: Fn, defaultState?: Awaited<ReturnType<Fn>>) {
    makeObservable(this);
    this.fn = fn;
    if (!isNil(defaultState)) {
      this.data = defaultState;
      this._defaultState = defaultState;
    }
  }
  // 当加载数据接口时，是否重置原来的数据，即设置为undefined
  public onLoadingReset = false;

  @computed get isReady() {
    return !isNil(this.data) || !isNil(this.error);
  }
  @action
  async fire(...args: Parameters<Fn>): Promise<Awaited<ReturnType<Fn>>> {
    const nonce = (this.nonce = this.nonce + 1);
    this.loading = true;
    const fn = this.fn;
    try {
      if (this.onLoadingReset) {
        runInAction(() => {
          this.data = this._defaultState;
          this.error = null;
        });
      }
      const data = await fn(...args);
      if (nonce === this.nonce) {
        runInAction(() => {
          this.data = data;
          this.error = null;
        });
      }
      return data;
    } catch (err) {
      if (this.nonce === nonce) {
        runInAction(() => {
          this.error = err;
          this.data = undefined;
        });
      }
      throw err;
    } finally {
      if (nonce === this.nonce) {
        runInAction(() => {
          this.loading = false;
          this.hasFired = true;
        });
      }
    }
  }
  throttle(interval: number) {
    const _this = this as any;
    if (!_this.____fn) {
      _this.____fn = this.fire;
    }
    const fn = throttle(_this.____fn.bind(this), interval);
    this.fire = async (...args: Parameters<Fn>) => {
      const rs = await fn(...args);
      if (rs === undefined) {
        // 这里只是为了让接口类型保持完整，但是数据上，肯定是有点奇怪的（即返回的结果和请求的参数不一致）
        // 但是现在基本上不会单独调用fire 然后获取其结果，特别是已经在已经throttle的情况下
        // 如果throttle 后需要再单独调用fire 获取结果，就使用 immediateFire 吧
        return this.data;
      }
      return rs;
    };
  }
  async immediateFire(
    ...args: Parameters<Fn>
  ): Promise<Awaited<ReturnType<Fn>>> {
    const fire = (this as any).____fn || this.fire;
    return fire.call(this, ...args);
  }
}

type FuncMayReturnCleaners = Array<() => unknown | Array<() => unknown>>;

export const combineRunClean = (...fns: FuncMayReturnCleaners) => {
  const stops = fns
    .reduce(
      (arr, fn) => (Array.isArray(fn) ? arr.concat(...fn) : arr.concat(fn)),
      [] as Array<() => unknown>
    )
    .map((fn) => fn());
  return () => stops.forEach((stop) => typeof stop === 'function' && stop());
};

export const cleanCounter = (...fns: FuncMayReturnCleaners) => {
  let count = 0;

  let fnList = fns;

  let stopper: undefined | (() => void);
  const _startAutoRun = () => {
    if (!stopper) {
      stopper = combineRunClean(...fnList);
    }
  };

  const runAndCount = () => {
    count = count + 1;
    let callFlag = false;
    _startAutoRun();
    return (immediate = false) => {
      // 只能被调用一次
      if (callFlag) {
        console.warn('销毁函数只能被调用一次');
        return;
      }
      callFlag = true;
      count = Math.max(0, count - 1);
      if (immediate === true || count === 0) {
        stopper?.();
        count = 0;
        stopper = undefined;
        return;
      }
      if (count < 0) {
        throw new Error('神奇的事情发生了');
      }
    };
  };

  // 在组件中时，想要启动内部的一些自动监听，就调用这个接口
  const useRunAndClean = () => {
    useEffect(() => {
      return runAndCount();
    }, []);
  };

  return {
    get runCount() {
      return count;
    },
    runAndCount,
    useRunAndClean,
    setFns(...fns2: FuncMayReturnCleaners) {
      if (stopper) {
        throw new Error('当已经开始，就不能再设置函数');
      }
      fnList = fns2;
    },
  };
};

export type CleanCounterType = ReturnType<typeof cleanCounter>;

export interface WithCleaner {
  cleaner: CleanCounterType;
}

export class ModelWithCleanCounter {
  public cleaner: CleanCounterType;
  constructor(...rest: FuncMayReturnCleaners) {
    this.cleaner = cleanCounter(...rest);
  }
}
/**
 * 带自监听的异步状态管理
 */
export class AsyncFnStateWithAutoRun<
  T = unknown,
  Params extends Record<string, unknown> = {},
> extends AsyncFnState<(params: Params) => Promise<T>> {
  public cleaner: CleanCounterType;

  @observable
  private _params?: Partial<Params> = undefined;

  private _paramsGetter: () => Params;

  @computed({
    equals: comparer.structural,
  })
  public get params() {
    return typeof this._paramsGetter === 'function'
      ? this._paramsGetter()
      : this._params;
  }
  constructor(
    fn: (params?: Params) => Promise<T>,
    paramsGetter?: () => Params | undefined,
    defaultState?: Awaited<T>
  ) {
    super(fn, defaultState);
    this._paramsGetter = paramsGetter;
    makeObservable(this);
    this.cleaner = cleanCounter(() =>
      autorun(() => {
        this.fire(this.params as Params);
      })
    );
  }

  @action
  setParams(params?: Partial<Params>, replace = false) {
    if (!params || replace) {
      this._params = params;
    } else {
      this._params = {
        ...this._params,
        ...params,
      };
    }
  }
  @action
  refresh() {
    this.fire(this.params as Params);
  }
}

/**
 * // 增加了before after调用的函数，以及可以返回一个clean函数，每次重新执行之前会调用一下
 * @param view 和autorun一样的函数，但是可以返回一个函数，在每次重新调用时会先提前调用，用于清理上一次run的时候的一些副作用（例如订阅事件）
 * @param param1
 */
export const autoRunWithClean = (
  view: (r: IReactionPublic) => any,
  {
    before,
    after,
    ...opts
  }: IAutorunOptions & {
    //  run之前调用的函数，会用action包裹，里面不会有track
    before?: () => void;
    // run 之后调用，用action包裹，不会有track
    after?: () => void;
  } = {}
) => {
  let fnReturn: unknown;
  if (before) {
    before = action(before);
  }
  if (after) {
    after = action(after);
  }
  return autorun((ir) => {
    if (typeof fnReturn === 'function') {
      action(fnReturn)();
      fnReturn = undefined;
    }
    before?.();
    fnReturn = view(ir);
    after?.();
  }, opts);
};
